// O seletor de SERVIÇO de cada linha do quadro de itens da proposta/venda
// (05/10/2026). Substitui o menu próprio que abria DENTRO da caixa com rolagem
// da tabela e ficava cortado (o defeito de 13/09): este abre por cima de tudo,
// como os outros seletores do sistema, e filtra pelo que a pessoa escreve —
// nome, código ou área, sem depender de acento.
//
// Digitou e não achou: "+ Cadastrar … como novo serviço" (abre o cadastro, com
// área, sub-área e código da NF, e a linha volta com ele escolhido) ou "Usar …
// como item livre" (a linha fica sem serviço e a coluna Área pede a área).
//
// O que a pessoa escreve não se perde: Enter sem nenhum serviço que case, ou
// sair do campo numa linha ainda sem descrição, vira a descrição da linha.
import { useMemo, useRef, useState } from 'react';
import { Button, Select } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { casaBusca, rotuloDaArea } from '../../utils/areasEmpresa';
import type { ItemVenda, ServicoDoCatalogo } from './areaDoItem';

// Serviço ainda sem área no catálogo aparece como "sem área" — e não com o
// texto antigo ("Outros"), que não é uma das áreas da empresa.
const rotuloDoServico = (s: ServicoDoCatalogo) =>
  `${s.codigo ? `[${s.codigo}] ` : ''}${s.nome || ''} · ${rotuloDaArea(s.setor_chave) || 'sem área'}`;
const estiloAcao: React.CSSProperties = { height: 'auto', whiteSpace: 'normal', textAlign: 'left', padding: '2px 4px' };

export function SeletorServico({ item, numero, servicos, onEscolher, onDesvincular, onCadastrar, onItemLivre }: {
  item: ItemVenda;
  numero: number;                              // posição da linha no quadro (1, 2, 3…)
  servicos: ServicoDoCatalogo[];
  onEscolher: (servico: ServicoDoCatalogo) => void;
  onDesvincular: () => void;
  onCadastrar: (nomeDigitado: string) => void;
  onItemLivre: (textoDigitado: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  // O menu fechou porque a pessoa JÁ decidiu (escolheu, cadastrou, item livre)?
  // Aí o texto digitado não vira descrição de novo.
  const decidiu = useRef(false);
  const opcoes = useMemo(() => {
    const lista = servicos.map(s => ({ value: String(s.id), label: rotuloDoServico(s) }));
    // Serviço da linha que saiu do catálogo (desativado): continua com nome na
    // tela, em vez do código interno dele.
    const atual = item.servico_id ? String(item.servico_id) : null;
    if (atual && !lista.some(o => o.value === atual)) {
      lista.unshift({ value: atual, label: 'Serviço que saiu do catálogo' });
    }
    return lista;
  }, [servicos, item.servico_id]);
  const digitado = busca.trim();
  const fechar = () => { decidiu.current = true; setAberto(false); setBusca(''); };

  return (
    <Select
      size="small" showSearch allowClear
      aria-label={`Serviço do catálogo — item ${numero}`}
      value={item.servico_id ? String(item.servico_id) : undefined}
      placeholder="Buscar serviço no catálogo…"
      options={opcoes}
      filterOption={(texto, opcao) => casaBusca(texto, opcao?.label)}
      searchValue={busca} onSearch={setBusca}
      open={aberto}
      onOpenChange={(v) => {
        if (v) { decidiu.current = false; setAberto(true); return; }
        // Saiu do campo sem escolher nada, numa linha ainda sem descrição: o que
        // foi escrito fica como a descrição (item livre) em vez de sumir.
        if (!decidiu.current && digitado && !String(item.descricao || '').trim()) onItemLivre(digitado);
        setAberto(false); setBusca('');
      }}
      onInputKeyDown={(e) => {
        // Esc é desistir: o que foi escrito não vira descrição.
        if (e.key === 'Escape') decidiu.current = true;
        // Enter sem nenhum serviço que case com o texto: vira item livre.
        if (e.key === 'Enter' && digitado && !opcoes.some(o => casaBusca(digitado, o.label))) {
          e.preventDefault();
          fechar(); onItemLivre(digitado);
        }
      }}
      onChange={(v) => {
        const escolhido = v ? servicos.find(s => String(s.id) === String(v)) : null;
        if (escolhido) onEscolher(escolhido);
        else if (!v) onDesvincular();
        fechar();
      }}
      notFoundContent={<span style={{ fontSize: 12, color: '#888' }}>Nenhum serviço do catálogo com esse nome.</span>}
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { minWidth: 'min(380px, 94vw)', maxWidth: 'min(620px, 94vw)' } } }}
      style={{ width: '100%' }}
      popupRender={(menu) => (
        <>
          {menu}
          <div style={{ borderTop: '1px solid #F0F1F5', padding: '6px 8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <Button type="link" size="small" icon={<PlusOutlined />} style={estiloAcao}
              onClick={() => { fechar(); onCadastrar(digitado); }}>
              {digitado ? `Cadastrar "${digitado}" como novo serviço` : 'Cadastrar novo serviço'}
            </Button>
            {digitado && (
              <Button type="link" size="small" icon={<EditOutlined />} style={estiloAcao}
                onClick={() => { fechar(); onItemLivre(digitado); }}>
                Usar "{digitado}" como item livre (sem serviço do catálogo)
              </Button>
            )}
          </div>
        </>
      )}
    />
  );
}
